import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useLang } from '../contexts/LangContext';
import { dashboardApi, DashboardData, DashboardStats } from '../api';
import { getDisplayStatusLabel, getDisplayStatusColor } from '../utils/statusLabels';
import { 
  HomeIcon, 
  UsersIcon, 
  ClipboardDocumentListIcon, 
  ChatBubbleLeftRightIcon,
  CurrencyDollarIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  ClockIcon,
  BuildingOfficeIcon,
  CalendarIcon,
  DocumentTextIcon,
  CogIcon
} from '@heroicons/react/24/outline';

const Dashboard: React.FC = () => {
  const { t, lang } = useLang();
  const [dashboardData, setDashboardData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await dashboardApi.getDashboardData();
      setDashboardData(data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Error loading dashboard data');
      console.error('Error loading dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('vi-VN', {
      style: 'currency',
      currency: 'VND'
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status: string) => getDisplayStatusColor(status);

  const translateStatus = (status: string) => getDisplayStatusLabel(status, t, lang);

  const getActivityIcon = (type: string) => {
    switch (type) {
      case 'task':
        return <ClipboardDocumentListIcon className="w-5 h-5" />;
      case 'feedback':
        return <DocumentTextIcon className="w-5 h-5" />;
      case 'booking':
        return <CalendarIcon className="w-5 h-5" />;
      case 'payment':
        return <CurrencyDollarIcon className="w-5 h-5" />;
      case 'chat':
        return <ChatBubbleLeftRightIcon className="w-5 h-5" />;
      case 'construction':
        return <BuildingOfficeIcon className="w-5 h-5" />;
      default:
        return <CogIcon className="w-5 h-5" />;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-red-600 text-xl mb-4">{error}</div>
          <button
            onClick={loadDashboardData}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
          >
            Thử lại
          </button>
        </div>
      </div>
    );
  }

  if (!dashboardData) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="text-gray-600 text-xl">Không có dữ liệu</div>
        </div>
      </div>
    );
  }

  const { stats, recentActivities, recentTasks, recentFeedbacks, recentBookings } = dashboardData;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
          <p className="text-gray-600 mt-1">Tổng quan hệ thống quản lý tòa nhà</p>
        </div>
        <div className="flex space-x-3">
          <button
            onClick={loadDashboardData}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 flex items-center space-x-2"
          >
            <CogIcon className="w-4 h-4" />
            <span>Làm mới</span>
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {/* Apartments */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Tổng căn hộ</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalApartments}</p>
            </div>
            <div className="p-3 bg-blue-100 rounded-full">
              <BuildingOfficeIcon className="w-6 h-6 text-blue-600" />
            </div>
          </div>
        </div>

        {/* Residents */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Tổng cư dân</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalResidents}</p>
            </div>
            <div className="p-3 bg-green-100 rounded-full">
              <UsersIcon className="w-6 h-6 text-green-600" />
            </div>
          </div>
        </div>

        {/* Staff */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Nhân viên</p>
              <p className="text-3xl font-bold text-gray-900">{stats.totalStaff}</p>
            </div>
            <div className="p-3 bg-purple-100 rounded-full">
              <UsersIcon className="w-6 h-6 text-purple-600" />
            </div>
          </div>
        </div>

        {/* Revenue */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600">Doanh thu tháng</p>
              <p className="text-3xl font-bold text-gray-900">{formatCurrency(stats.monthlyRevenue)}</p>
            </div>
            <div className="p-3 bg-yellow-100 rounded-full">
              <CurrencyDollarIcon className="w-6 h-6 text-yellow-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Detailed Statistics */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Tasks */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Nhiệm vụ</h3>
            <Link to="/tasks" className="text-primary-600 hover:text-primary-700 text-sm">
              Xem tất cả
            </Link>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Tổng cộng</span>
              <span className="font-semibold">{stats.totalTasks}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Đang chờ</span>
              <span className="font-semibold text-yellow-600">{stats.pendingTasks}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Hoàn thành</span>
              <span className="font-semibold text-green-600">{stats.completedTasks}</span>
            </div>
          </div>
        </div>

        {/* Feedbacks */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Phản hồi</h3>
            <Link to="/feedbacks" className="text-primary-600 hover:text-primary-700 text-sm">
              Xem tất cả
            </Link>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Tổng cộng</span>
              <span className="font-semibold">{stats.totalFeedbacks}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Đang chờ</span>
              <span className="font-semibold text-yellow-600">{stats.pendingFeedbacks}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Hoàn thành</span>
              <span className="font-semibold text-green-600">{stats.completedFeedbacks}</span>
            </div>
          </div>
        </div>

        {/* Bills */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Phiếu phí dịch vụ</h3>
            <Link to="/transaction-report" className="text-primary-600 hover:text-primary-700 text-sm">
              Xem tất cả
            </Link>
          </div>
          <div className="space-y-3">
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Tổng cộng</span>
              <span className="font-semibold">{stats.totalBills}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Đã thanh toán</span>
              <span className="font-semibold text-green-600">{stats.paidBills}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Chưa thanh toán</span>
              <span className="font-semibold text-yellow-600">{stats.unpaidBills}</span>
            </div>
            <div className="flex justify-between items-center">
              <span className="text-gray-600">Quá hạn</span>
              <span className="font-semibold text-red-600">{stats.overdueBills}</span>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activities and Lists */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Activities */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Hoạt động gần đây</h3>
            <Link to="/activities" className="text-primary-600 hover:text-primary-700 text-sm">
              Xem tất cả
            </Link>
          </div>
          <div className="space-y-4">
            {recentActivities.slice(0, 5).map((activity) => (
              <div key={activity.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50">
                <div className="flex-shrink-0 mt-1">
                  <div className="p-2 bg-gray-100 rounded-full">
                    {getActivityIcon(activity.type)}
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{activity.title}</p>
                  <p className="text-sm text-gray-500 truncate">{activity.description}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-400">{activity.userName}</span>
                    <span className="text-xs text-gray-400">{formatDate(activity.timestamp)}</span>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(activity.status)}`}>
                    {translateStatus(activity.status)}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Tasks */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Nhiệm vụ gần đây</h3>
            <Link to="/tasks" className="text-primary-600 hover:text-primary-700 text-sm">
              Xem tất cả
            </Link>
          </div>
          <div className="space-y-4">
            {recentTasks.slice(0, 5).map((task) => (
              <div key={task.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50">
                <div className="flex-shrink-0 mt-1">
                  <ClipboardDocumentListIcon className="w-5 h-5 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{task.title}</p>
                  <p className="text-sm text-gray-500">Giao cho: {task.assignedTo}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-400">Hạn: {formatDate(task.dueDate)}</span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(task.status)}`}>
                      {translateStatus(task.status)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Recent Feedbacks and Bookings */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Feedbacks */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Phản hồi gần đây</h3>
            <Link to="/feedbacks" className="text-primary-600 hover:text-primary-700 text-sm">
              Xem tất cả
            </Link>
          </div>
          <div className="space-y-4">
            {recentFeedbacks.slice(0, 5).map((feedback) => (
              <div key={feedback.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50">
                <div className="flex-shrink-0 mt-1">
                  <DocumentTextIcon className="w-5 h-5 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{feedback.title}</p>
                  <p className="text-sm text-gray-500">{feedback.category} • {feedback.apartmentCode}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-400">{formatDate(feedback.createdAt)}</span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(feedback.status)}`}>
                      {translateStatus(feedback.status)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Recent Bookings */}
        <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Đặt chỗ gần đây</h3>
            <Link to="/building-utility-bookings" className="text-primary-600 hover:text-primary-700 text-sm">
              Xem tất cả
            </Link>
          </div>
          <div className="space-y-4">
            {recentBookings.slice(0, 5).map((booking) => (
              <div key={booking.id} className="flex items-start space-x-3 p-3 rounded-lg hover:bg-gray-50">
                <div className="flex-shrink-0 mt-1">
                  <CalendarIcon className="w-5 h-5 text-gray-400" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{booking.utilityName}</p>
                  <p className="text-sm text-gray-500">{booking.apartmentCode} • {formatCurrency(booking.totalAmount)}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-xs text-gray-400">{formatDate(booking.startTime)}</span>
                    <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(booking.status)}`}>
                      {translateStatus(booking.status)}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-lg shadow-md p-6 border border-gray-200">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Thao tác nhanh</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Link
            to="/tasks/add"
            className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
          >
            <ClipboardDocumentListIcon className="w-8 h-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Tạo nhiệm vụ</span>
          </Link>
          <Link
            to="/feedbacks"
            className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
          >
            <DocumentTextIcon className="w-8 h-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Xem phản hồi</span>
          </Link>
          <Link
            to="/building-utility-bookings"
            className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
          >
            <CalendarIcon className="w-8 h-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Quản lý đặt chỗ</span>
          </Link>
          <Link
            to="/transaction-report"
            className="flex flex-col items-center p-4 rounded-lg border border-gray-200 hover:border-primary-300 hover:bg-primary-50 transition-colors"
          >
            <CurrencyDollarIcon className="w-8 h-8 text-primary-600 mb-2" />
            <span className="text-sm font-medium text-gray-900">Xem hóa đơn</span>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Dashboard; 